import React from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { BadgeCheck } from 'lucide-react';
import { formatPrice } from '../utils/productScanner';
import { optimizedImage } from '../utils/images';
import type { PurchaseEvent } from '../types/purchase';
interface PurchaseToastProps {
  event: PurchaseEvent | null;
  /** Raises the toast so it clears the sticky checkout bar. */
  offsetForStickyBar?: boolean;
}
export function PurchaseToast({
  event,
  offsetForStickyBar = true
}: PurchaseToastProps) {
  const reduceMotion = useReducedMotion();
  const enterOffset = reduceMotion ? 0 : 26;
  return <div aria-live="polite" aria-atomic="true" className={`pointer-events-none fixed inset-x-0 z-50 flex justify-center px-4 ${offsetForStickyBar ? 'bottom-[86px] sm:bottom-[92px]' : 'bottom-6'}`} data-id="element-207" data-mp="2zwfpf67084nq">
      <AnimatePresence data-id="element-208" data-mp="f79xkoat5xfx">
        {event && <motion.div key={event.id} initial={{
        opacity: 0,
        y: enterOffset,
        scale: reduceMotion ? 1 : 0.97
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: enterOffset,
        scale: 1
      }} transition={{
        duration: reduceMotion ? 0.15 : 0.45,
        ease: [0.22, 1, 0.36, 1]
      }} className="pointer-events-auto flex w-full max-w-[380px] items-center gap-3 rounded-2xl border border-gold-500/25 bg-noir-800/95 p-3 shadow-[0_20px_45px_-18px_rgba(0,0,0,0.95)] backdrop-blur-md" data-id="element-209" data-mp="b2sdsp4g7k55">
            {event.product.thumbnailUrl ? <img src={optimizedImage(event.product.thumbnailUrl, 120)} onError={imageEvent => {
          const original = event.product.thumbnailUrl;
          if (original) imageEvent.currentTarget.src = original;
        }} alt="" aria-hidden="true" width={40} height={56} loading="lazy" decoding="async" className="h-14 w-10 shrink-0 rounded-[3px] object-cover ring-1 ring-gold-500/25" data-id="element-210" data-mp="1ffihxjwgalk0" /> : <span aria-hidden="true" className="flex h-14 w-10 shrink-0 items-center justify-center rounded-[3px] bg-noir-700 ring-1 ring-gold-500/25" data-id="element-211" data-mp="1v0u0neged6gq">
                <BadgeCheck className="h-4 w-4 text-gold-400" data-id="element-212" data-mp="24uublwyd15xd" />
              </span>}

            <div className="min-w-0 flex-1 text-left" data-id="element-213" data-mp="1h59hqj0o4o9b">
              <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-crimson-500" data-id="element-214" data-mp="1c53wplowm007">
                <span className="h-1.5 w-1.5 rounded-full bg-crimson-500 animate-pulseDot" data-id="element-215" data-mp="1q5en6nmaxcku" />
                Compra reciente
              </p>
              <p className="mt-1 truncate text-[13px] font-semibold text-cream" data-id="element-216" data-mp="31iqk0ejn8uxi">
                {event.name} en {event.city}
              </p>
              <p className="mt-0.5 truncate text-[12px] text-cream/60" data-id="element-217" data-mp="2wj47y8fjxkqa">
                acaba de comprar{' '}
                <span className="text-cream/85" data-id="element-218" data-mp="34zd002ftz0n9">{event.product.title}</span>
              </p>
              <p className="mt-1 flex items-center gap-1.5 truncate text-[11px] text-cream/45" data-id="element-219" data-mp="3942tjkn0o350">
                {event.product.editionLabel && <>
                    <span className="truncate" data-id="element-220" data-mp="3011oln9w0i2x">{event.product.editionLabel}</span>
                    <span aria-hidden="true" data-id="element-221" data-mp="3nrny7izjiaf1">·</span>
                  </>}
                <span className="font-semibold text-gold-400" data-id="element-222" data-mp="3sqim2zcd6p60">
                  {event.product.priceText || formatPrice(event.product.priceNumber)}
                </span>
              </p>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}